import {
  useAdsResearchStore,
  type AdsDistilledResearch,
  type AdsResearchProductInput,
  type UgcBrief,
} from "./useAdsResearchStore";
import { normalizeAdCopy } from "./adsResearchUtils";
import type { ManagedToolResultEvent } from "@/features/companion";

export type ScoutPhaseEvent = {
  type: "scout_phase";
  phase: "query_generation" | "collection" | "distillation" | "ad_generation" | "ugc_brief";
  status: "running" | "complete" | "failed";
  progress?: { completed: number; total: number; track?: string };
  data?: unknown;
  error?: string;
};

export function buildScoutResearchMessage(productInput: AdsResearchProductInput): string {
  return [
    `Research ${productInput.productTitle}`,
    "",
    "Use this structured product input:",
    "```json",
    JSON.stringify(productInput, null, 2),
    "```",
  ].join("\n");
}

function parseToolOutput(output: unknown): unknown {
  if (typeof output !== "string") return output;
  try {
    return JSON.parse(output);
  } catch {
    return output;
  }
}

function asRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}

function readDataEnvelope(output: unknown): { ok: boolean; data?: Record<string, unknown>; error?: string } | null {
  const parsed = parseToolOutput(output);
  if (!parsed || typeof parsed !== "object") return null;
  const rec = parsed as Record<string, unknown>;
  if (typeof rec.ok !== "boolean") return null;
  return {
    ok: rec.ok,
    data: asRecord(rec.data),
    error: typeof rec.error === "string" ? rec.error : undefined,
  };
}

export function applyScoutPhaseEvent(event: ScoutPhaseEvent) {
  const store = useAdsResearchStore.getState();
  const { phase, status, data, progress, error } = event;

  if (status === "running") {
    store.setStatus("running");
    store.setError(null);
  }

  if (phase === "query_generation") {
    store.setPhase("query_generation");
    const rec = asRecord(data);
    if (status === "complete" && typeof rec.query_count === "number") {
      store.setQueryCount(rec.query_count);
    }
    return;
  }

  if (phase === "collection") {
    store.setPhase("collection");
    if (progress) {
      store.setProgress({
        completed: progress.completed ?? 0,
        total: progress.total ?? 0,
        track: progress.track,
      });
    }
    const rec = asRecord(data);
    if (status === "complete" && typeof rec.successful === "number") {
      store.setCollectionSuccessful(rec.successful);
    }
    return;
  }

  if (phase === "distillation") {
    store.setPhase("distillation");
    const rec = asRecord(data);
    if (status === "complete" && rec.distilled) {
      store.setDistilledResearch(rec.distilled as AdsDistilledResearch);
    }
    return;
  }

  if (phase === "ad_generation") {
    store.setPhase("ad_generation");
    const rec = asRecord(data);
    if (status === "complete" && rec.adCopy) {
      store.setAdCopy(normalizeAdCopy(rec.adCopy));
    }
    return;
  }

  if (phase === "ugc_brief") {
    store.setPhase("ugc_brief");
    const rec = asRecord(data);
    if (status === "complete" && rec.ugcBrief) {
      store.setUgcBrief(rec.ugcBrief as UgcBrief);
    } else if (status === "failed") {
      console.warn("Scout UGC brief generation failed (non-fatal):", error);
    }
  }
}

export function applyScoutToolResult(event: ManagedToolResultEvent, sessionId: string | null) {
  const toolName = event.name;
  if (!toolName) return;
  if (
    ![
      "run_research",
      "redistill",
      "regenerate_ad_copy",
      "regenerate_ugc_brief",
      "add_user_context",
      "recollect_data",
      "get_current_state",
    ].includes(toolName)
  ) {
    return;
  }

  const envelope = readDataEnvelope(event.output);
  if (!envelope) return;

  const store = useAdsResearchStore.getState();
  if (!envelope.ok) {
    store.setError(envelope.error ?? `${toolName} failed`);
    store.setStatus("error");
    return;
  }

  const data = envelope.data ?? {};
  if (typeof data.artifactId === "string" && data.artifactId.length > 0) {
    store.setResearchBriefId(data.artifactId);
    if (sessionId) store.setScoutSessionForArtifact(data.artifactId, sessionId);
  }
  if (typeof data.version === "number") {
    store.setScoutVersion(data.version);
  }
  if (data.distilled) {
    store.setDistilledResearch(data.distilled as AdsDistilledResearch);
  }
  if (data.adCopy) {
    store.setAdCopy(normalizeAdCopy(data.adCopy));
  }
  if (Object.prototype.hasOwnProperty.call(data, "ugcBrief")) {
    store.setUgcBrief((data.ugcBrief as UgcBrief | undefined) ?? null);
  }
  if (Array.isArray((data as Record<string, unknown>).inspirationAds)) {
    store.setInspirationAds(
      (data as Record<string, unknown>).inspirationAds as Parameters<
        typeof store.setInspirationAds
      >[0],
    );
  }
  if (typeof data.successful === "number") {
    store.setCollectionSuccessful(data.successful);
  }

  if (toolName !== "get_current_state") {
    store.setPhase(null);
    store.setProgress(null);
    store.setStatus("complete");
  }
}
